import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import OutlinedInput from "@mui/material/OutlinedInput";
import moment from "moment";

const MonthYearPicker = ({ value, onChange, label, mt }) => {
  const today = moment();
  const currentYear = today.year();
  const currentMonth = today.format("MM");

  // Determine cutoff based on current date
  const monthOffset = today.date() > 4 ? 1 : 2;
  const cutoffMonth = moment().subtract(monthOffset, "months").format("MM");

  const years = Array.from(
    { length: currentYear - 2022 + 1 },
    (_, i) => 2022 + i
  );

  const allMonths = [
    { value: "01", label: "January" },
    { value: "02", label: "February" },
    { value: "03", label: "March" },
    { value: "04", label: "April" },
    { value: "05", label: "May" },
    { value: "06", label: "June" },
    { value: "07", label: "July" },
    { value: "08", label: "August" },
    { value: "09", label: "September" },
    { value: "10", label: "October" },
    { value: "11", label: "November" },
    { value: "12", label: "December" },
  ];

  const filteredMonths =
    parseInt(value.year) === currentYear
      ? allMonths.filter((m) => parseInt(m.value) <= parseInt(cutoffMonth))
      : allMonths;

  // If selected month is invalid after year change, reset it
  useEffect(() => {
    if (
      parseInt(value.year) === currentYear &&
      parseInt(value.month) > parseInt(cutoffMonth)
    ) {
      onChange({ ...value, month: cutoffMonth });
    }
  }, [value.year]);

  const handleMonthChange = (e) => {
    onChange({ ...value, month: e.target.value });
  };

  const handleYearChange = (e) => {
    onChange({ ...value, year: e.target.value });
  };

  // Custom styles to ensure consistent border in all states
  const selectStyles = {
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid #e8e8e8",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "1px solid #e8e8e8",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "1px solid #e8e8e8",
      boxShadow: "none",
    },
    "&.Mui-error .MuiOutlinedInput-notchedOutline": {
      border: "1px solid #e8e8e8",
    },
  };

  // Common style for placeholder and selected text
  const placeholderStyle = {
    fontWeight: "normal",
    color: "rgba(0, 0, 0, 0.87)", // Regular text color
    fontStyle: "normal", // Remove italic style
  };

  return (
    <Box sx={{ display: "flex", gap: 1, mt: mt || 0 }}>
      <FormControl sx={{ minWidth: 100 }} size="small">
        <Select
          id={`${label}-year`}
          value={value.year}
          onChange={handleYearChange}
          displayEmpty
          input={<OutlinedInput notched={false} sx={selectStyles} />}
          renderValue={(selected) => {
            if (!selected) {
              return <span style={placeholderStyle}>Year</span>;
            }
            return selected;
          }}
        >
          <MenuItem disabled value="">
            <span style={placeholderStyle}>Year</span>
          </MenuItem>
          {years.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ minWidth: 120 }} size="small">
        <Select
          id={`${label}-month`}
          value={value.month}
          onChange={handleMonthChange}
          displayEmpty
          input={<OutlinedInput notched={false} sx={selectStyles} />}
          renderValue={(selected) => {
            if (!selected) {
              return <span style={placeholderStyle}>Month</span>;
            }
            return filteredMonths.find((month) => month.value === selected)
              ?.label;
          }}
        >
          <MenuItem disabled value="">
            <span style={placeholderStyle}>Month</span>
          </MenuItem>
          {filteredMonths.map((month) => (
            <MenuItem key={month.value} value={month.value}>
              {month.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
};

export default MonthYearPicker;
